// src/pages/attendance/utils/locationService.js
import { apiFetch } from "../../../context/AuthContext";

export async function getAllowedArea(infinityCode, baseUrl) {
  try {
    const url = `${baseUrl}/employees/location/${infinityCode}/schedule`;
    const response = await apiFetch(url, { method: "GET" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    if (!data?.success) throw new Error("No location schedule data");

    const { locationPoint, coordinates, locationName, schedule } = data.data;
    if (!locationPoint) throw new Error("No work location assigned for today");
    if (!coordinates?.latitude || !coordinates?.longitude) throw new Error("Invalid coordinates");

    const lat = parseFloat(coordinates.latitude);
    const lng = parseFloat(coordinates.longitude);
    const radius = parseFloat(coordinates.radius);

    return { lat, lng, radius, locationPoint, locationName, dayOfWeek: new Date().getDay(), schedule };
  } catch (error) {
    // fallback
    const fallbackResponse = await apiFetch(`${baseUrl}/employees/location/${infinityCode}`, { method: "GET" });
    if (!fallbackResponse.ok) throw new Error(`Fallback HTTP ${fallbackResponse.status}`);
    const data = await fallbackResponse.json();

    const latitude  = data.Latitude1  || data.Latitude2  || data.Latitude3  || data.Latitude4;
    const longitude = data.Longitude1 || data.Longitude2 || data.Longitude3 || data.Longitude4;
    if (!latitude || !longitude) throw new Error("No valid coordinates in fallback");

    return { lat: parseFloat(latitude), lng: parseFloat(longitude), radius: 300, locationPoint: 1, isFallback: true };
  }
}

export function calculateAccurateDistance(lat1, lon1, lat2, lon2) {
  for (const v of [lat1, lon1, lat2, lon2])
    if (v == null || typeof v !== "number" || isNaN(v)) return null;

  const a = 6378137.0, b = 6356752.314245, f = 1 / 298.257223563;
  const L  = ((lon2 - lon1) * Math.PI) / 180;
  const U1 = Math.atan((1 - f) * Math.tan((lat1 * Math.PI) / 180));
  const U2 = Math.atan((1 - f) * Math.tan((lat2 * Math.PI) / 180));
  const sinU1 = Math.sin(U1), cosU1 = Math.cos(U1);
  const sinU2 = Math.sin(U2), cosU2 = Math.cos(U2);

  let lambda = L, lambdaP, iterLimit = 100;
  let cosSqAlpha, sinSigma, cos2SigmaM, cosSigma, sigma;

  do {
    const sinL = Math.sin(lambda), cosL = Math.cos(lambda);
    sinSigma = Math.sqrt((cosU2*sinL)**2 + (cosU1*sinU2 - sinU1*cosU2*cosL)**2);
    if (sinSigma === 0) return 0;
    cosSigma = sinU1*sinU2 + cosU1*cosU2*cosL;
    sigma = Math.atan2(sinSigma, cosSigma);
    const sinAlpha = (cosU1*cosU2*sinL) / sinSigma;
    cosSqAlpha = 1 - sinAlpha**2;
    cos2SigmaM = cosSigma - (2*sinU1*sinU2) / cosSqAlpha;
    if (isNaN(cos2SigmaM)) cos2SigmaM = 0;
    const C = (f/16)*cosSqAlpha*(4 + f*(4 - 3*cosSqAlpha));
    lambdaP = lambda;
    lambda = L + (1-C)*f*sinAlpha*(sigma + C*sinSigma*(cos2SigmaM + C*cosSigma*(-1 + 2*cos2SigmaM**2)));
  } while (Math.abs(lambda - lambdaP) > 1e-12 && --iterLimit > 0);

  if (iterLimit === 0) return calculateHaversineDistance(lat1, lon1, lat2, lon2);

  const uSq = (cosSqAlpha*(a**2 - b**2)) / b**2;
  const A2  = 1 + (uSq/16384)*(4096 + uSq*(-768 + uSq*(320 - 175*uSq)));
  const B2  = (uSq/1024)*(256 + uSq*(-128 + uSq*(74 - 47*uSq)));
  const ds  = B2*sinSigma*(cos2SigmaM + (B2/4)*(cosSigma*(-1 + 2*cos2SigmaM**2) - (B2/6)*cos2SigmaM*(-3 + 4*sinSigma**2)*(-3 + 4*cos2SigmaM**2)));
  return b * A2 * (sigma - ds);
}

export function calculateHaversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000, rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(lat2 - lat1), dLon = rad(lon2 - lon1);
  const a = Math.sin(dLat/2)**2 + Math.cos(rad(lat1))*Math.cos(rad(lat2))*Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export class EnhancedLocationTracker {
  constructor() { this.watchId = null; this.locationSamples = []; this.isTracking = false; }

  async getAveragedLocation(sampleSize = 3) {
    const samples = [];
    for (let i = 0; i < sampleSize; i++) {
      try {
        samples.push(await this._getLocationWithTimeout({ enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }));
        if (i < sampleSize - 1) await this._delay(1000);
      } catch { /* skip bad sample */ }
    }
    if (samples.length === 0) throw new Error("Failed to obtain any location samples");
    return this._calculateWeightedAverage(samples);
  }

  async getAccurateLocation() {
    if (!navigator.geolocation) throw new Error("Geolocation not supported");
    for (const opts of [
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 10000 },
    ]) {
      try { return await this._getLocationWithTimeout(opts); } catch { /* try next */ }
    }
    throw new Error("All location attempts failed");
  }

  startContinuousTracking(onUpdate, onError) {
    if (this.isTracking) return;
    this.isTracking = true;
    this.locationSamples = [];
    this.watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy, timestamp: pos.timestamp };
        this.locationSamples.push(loc);
        if (this.locationSamples.length > 10) this.locationSamples.shift();
        const averaged = this._calculateWeightedAverage(this.locationSamples);
        onUpdate?.({ location: averaged, accuracyLevel: this._getAccuracyLevel(averaged.accuracy), sampleCount: this.locationSamples.length });
      },
      (err) => onError?.(err),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }

  stopContinuousTracking() {
    if (this.watchId !== null) { navigator.geolocation.clearWatch(this.watchId); this.watchId = null; this.isTracking = false; this.locationSamples = []; }
  }

  _getLocationWithTimeout(options) {
    return new Promise((resolve, reject) => {
      const id = setTimeout(() => reject(new Error("Timeout")), options.timeout);
      navigator.geolocation.getCurrentPosition(
        (pos) => { clearTimeout(id); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy, timestamp: pos.timestamp }); },
        (err) => { clearTimeout(id); reject(err); },
        options
      );
    });
  }

  _calculateWeightedAverage(samples) {
    if (samples.length === 1) return samples[0];
    const weights = samples.map((s) => 1 / (s.accuracy + 1));
    const total   = weights.reduce((s, w) => s + w, 0);
    return {
      lat:      samples.reduce((s, v, i) => s + v.lat * weights[i], 0) / total,
      lng:      samples.reduce((s, v, i) => s + v.lng * weights[i], 0) / total,
      accuracy: samples.reduce((s, v, i) => s + v.accuracy * weights[i], 0) / total,
      timestamp: samples[samples.length - 1].timestamp,
      sampleCount: samples.length,
    };
  }

  _getAccuracyLevel(a) {
    if (a <= 10) return "excellent"; if (a <= 20) return "good";
    if (a <= 50) return "acceptable"; if (a <= 100) return "poor";
    return "very poor";
  }

  _delay(ms) { return new Promise((r) => setTimeout(r, ms)); }
}

export const locationTracker = new EnhancedLocationTracker();